/**
 * v0.11.0 Card — single white card style replacing the dozens of
 * inline-styled cards across the kid app.
 *
 *   <Card>...</Card>                              // default white
 *   <Card variant="bond">...</Card>               // pink (wishes / bond)
 *   <Card variant="success">...</Card>            // green (done / habits)
 *   <Card variant="imagine">...</Card>            // purple (imagination)
 *   <Card variant="learn">...</Card>              // blue (quiz)
 *   <Card variant="danger">...</Card>             // red soft warning
 *   <Card accent>...</Card>                       // 4px top color band
 *   <Card accent accentSide="left">...</Card>     // 4px left color band
 *
 * Always 12px radius, 0.5px border, 16px padding. No box-shadow.
 */
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

type CardVariant = 'default' | 'bond' | 'success' | 'imagine' | 'learn' | 'danger';

interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style'> {
  variant?: CardVariant;
  accent?: boolean;
  accentSide?: 'top' | 'left';
  padded?: boolean;          // default true; pass false for image-only cards
  style?: CSSProperties;
  children?: ReactNode;
}

const VARIANT_STYLES: Record<CardVariant, { bg: string; border: string }> = {
  default: { bg: 'var(--xpb-surface)',     border: 'var(--xpb-border)' },
  bond:    { bg: 'var(--xpb-bond-bg)',     border: 'var(--xpb-bond-border)' },
  success: { bg: 'var(--xpb-success-bg)',  border: 'var(--xpb-success)' },
  imagine: { bg: 'var(--xpb-imagine-bg)',  border: 'var(--xpb-imagine)' },
  learn:   { bg: 'var(--xpb-learn-bg)',    border: 'var(--xpb-learn)' },
  danger:  { bg: 'var(--xpb-danger-bg)',   border: 'var(--xpb-danger)' },
};

export function Card({
  variant = 'default',
  accent = false,
  accentSide = 'top',
  padded = true,
  style,
  children,
  ...rest
}: CardProps) {
  const v = VARIANT_STYLES[variant];
  const accentStyle: CSSProperties = accent
    ? accentSide === 'top'
      ? { borderTop: `4px solid ${v.border}` }
      : { borderLeft: `4px solid ${v.border}` }
    : {};

  return (
    <div
      style={{
        background: v.bg,
        border: `0.5px solid ${v.border}`,
        borderRadius: 12,
        padding: padded ? 16 : 0,
        position: 'relative',
        ...accentStyle,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}
